import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {classNames} from "primereact/utils";
import {Badge} from "primereact/badge";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks";
import {Paginator} from "primereact/paginator";
import {MultiSelect} from "primereact/multiselect";
import {UPLOAD_AUDIO_LOADING} from "../../../redux/types/types";
import {InputText} from "primereact/inputtext";
import DateRangeFilterTemplate, {
    getDefaultDateRangeFilterValue,
    getDefaultFilterEndTime,
    getDefaultFilterStartDate,
    getDefaultFilterStartTime,
    type DateRangeFilterValue,
} from "./dateRangeFilterTemplate";
import {getAllSimpleUsers} from "../../../redux/api/api";
import {getUsersManagerUserBranchesData} from "../../../redux/api/apiUsersList";
import {formatBranchDisplayLabel} from "../../../utils/formatBranchDisplayLabel";
import {getAudioUrlForPlayer, setAudioUnavailable} from "../../../redux/actions/actionsMonitoringList";
import {fetchSimpleUsersControls} from "../../../redux/api/apiAudioControl";
import {
    getQualityControlStatisticsUserSortFields,
    getQualityControlStatisticsWithPaginationFilterAndSorting,
    getQualityControlStatisticToUserDetail,
} from "../../../redux/api/apiQualityControlStatistics";
import {
    resetQualityControlStatisticFilters,
    setQualityControlStatisticsCurrentPage,
    setQualityControlStatisticsCurrentPageSize,
    setQualityControlStatisticsFilter,
    setQualityControlStatisticsUiFilters,
} from "../../../redux/actions/actionsQualityControlStatistics";
import {
    GET_QUALITY_CONTROL_STATISTICS,
    GET_QUALITY_CONTROL_STATISTICS_PAGINATION,
    INITIAL_QUALITY_CONTROL_STATISTICS_DATA,
} from "../../../redux/types/typesQualityControlStatistics";
import StatisticsListDetails from "../StatisticsList/StatisticsListDetails";
import QualityControlStatisticsListNav from "./QualityControlStatisticsListNav";
import {
    getCallIdFromQualityControlRow,
    getQualityControlEvaluationTypeLabel,
    getQualityControlStatisticsFieldLabel,
    isRemovedQualityControlStatisticsField,
    QUALITY_CONTROL_EVALUATION_TYPE_OPTIONS,
    QUALITY_CONTROL_STATISTICS_FILTER_KEYS,
    QUALITY_CONTROL_STATUS_OPTIONS,
    expandQualityControlStatusFilterValues,
    toQualityControlApiSortField,
} from "./qualityControlStatisticsFields";
import {
    getQuestionnaireStatusMeta,
    normalizeSimpleUsersControls,
    type QualityControlSimpleOption,
} from "../ManualEvaluationDialog/manualEvaluationQuestionnaires";
import {renderCallTypeActivityIcon} from "../callTypeActivityIcon";
import {
    areSearchParamsEqual,
    buildQualityControlSearchParams,
    readInitialQualityControlUrlState
} from "../../../utils/qualityControlUrlState";

const QualityControlStatisticsList = (props: any) => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [initialQualityControlUrlState] = useState(readInitialQualityControlUrlState);
    const statisticsList = useAppSelector(state => state.QualityControlStatisticsReducer?.statisticsList);
    const userQualityControlSortFields: any = useAppSelector(state => state.RolesReducer.userQualityControlSortFields);
    const statisticData = useAppSelector(state => state.QualityControlStatisticsReducer?.statisticData);
    const paginationSize = useAppSelector(state => state.QualityControlStatisticsReducer.paginationSize);
    const pagination = useAppSelector(state => state.QualityControlStatisticsReducer.pagination);
    const loader = useAppSelector(state => state.QualityControlStatisticsReducer.loading);
    const paginationPage = useAppSelector(state => state.QualityControlStatisticsReducer.paginationPage);
    const clearFilters = useAppSelector(state => state.QualityControlStatisticsReducer.clearFilters)
    const user = useAppSelector(state => state.UsersReducer.userData);
    // @ts-ignore
    const activeClick = useAppSelector(state => state.UsersListReducer.activeClick);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const hasUsersAccess = !!rolesVision?.hasOwnProperty('Users');
    const hasBranchAccess = !!rolesVision?.hasOwnProperty('Branch');
    // @ts-ignore
    const usersData = useAppSelector(state => state.CompaniesListReducer.allSimpleUsers);
    const branches = useAppSelector(state => state.UsersListReducer.branches);
    const statusDictionary = useAppSelector(state => state.MonitoringListReducer.statusDictionary);
    const ownerTypeDictionary = useAppSelector(state => state.MonitoringListReducer.ownerTypeDictionary);
    const forcedClosingDictionary = useAppSelector(state => state.MonitoringListReducer.forcedClosingDictionary);
    const pageChanged = useAppSelector(state => state.QualityControlStatisticsReducer.pageChanged);

    const [first, setFirst] = useState(() => (initialQualityControlUrlState.page - 1) * initialQualityControlUrlState.size);
    const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
    const [orderBy, setOrderBy] = useState<any>(initialQualityControlUrlState.orderBy);
    const [isDescFilter, setIsDescFilter] = useState<boolean>(initialQualityControlUrlState.isDesc);
    const [sortOrder, setSortOrder] = useState<number>(initialQualityControlUrlState.sortOrder);
    const [startTime, setStartTime] = useState<any>(initialQualityControlUrlState.startTime || new Date("2024-10-29 00:00:00"));
    const [endTime, setEndTime] = useState<any>(() => {
        if (initialQualityControlUrlState.endTime) return initialQualityControlUrlState.endTime;
        const d = new Date();
        d.setHours(23, 59, 59, 0);
        return d;
    });
    const [filterDateTime, setFilterDateTime] = useState<any>(initialQualityControlUrlState.filterDateTime || new Date());
    const [filterEndDateTime, setFilterEndDateTime] = useState<any>(initialQualityControlUrlState.filterEndDateTime || new Date());
    const [visible, setVisible] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [key, setKey] = useState(1);
    const [totalItems, setTotalItems] = useState<number>(paginationSize);
    const [hasStartFilter, setHasStartFilter] = useState<boolean>(false);
    const [allTimeChecked, setAllTimeChecked] = useState<boolean | null>(initialQualityControlUrlState.allTimeChecked);
    const [questionnaireOptions, setQuestionnaireOptions] = useState<QualityControlSimpleOption[]>([]);
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | null>(initialQualityControlUrlState.questionnaireId);
    const [filterQualityControlStatus, setFilterQualityControlStatus] = useState<any>(initialQualityControlUrlState.filterQualityControlStatus);
    const [filterStatus, setFilterStatus] = useState<any>(initialQualityControlUrlState.filterStatus);
    const [filterOwnerType, setFilterOwnerType] = useState<any>(initialQualityControlUrlState.filterOwnerType);
    const [filterForcedClosing, setFilterForcedClosing] = useState<any>(initialQualityControlUrlState.filterForcedClosing);
    const [filterBranchName, setFilterBranchName] = useState<any>(initialQualityControlUrlState.filterBranchName);
    const [filterIsManual, setFilterIsManual] = useState<any>(initialQualityControlUrlState.filterIsManual);
    const [filterQualityControlId, setFilterQualityControlId] = useState<any>(initialQualityControlUrlState.filterQualityControlId);
    const [filterFullName, setFilterFullName] = useState<any>(initialQualityControlUrlState.filterFullName);
    const [filterAni, setFilterAni] = useState<any>(initialQualityControlUrlState.filterAni);
    const [controlEndRange, setControlEndRange] = useState<DateRangeFilterValue>(() => (
        initialQualityControlUrlState.hasControlEndFilter
            ? {
                fromDate: initialQualityControlUrlState.controlEndFromDate || getDefaultFilterStartDate(),
                toDate: initialQualityControlUrlState.controlEndToDate || getDefaultFilterStartDate(),
                fromTime: initialQualityControlUrlState.controlEndFromTime || getDefaultFilterStartTime(),
                toTime: initialQualityControlUrlState.controlEndToTime || getDefaultFilterEndTime(),
            }
            : getDefaultDateRangeFilterValue()
    ));
    const [hasControlEndFilter, setHasControlEndFilter] = useState(initialQualityControlUrlState.hasControlEndFilter);
    const [controlEndFilterVersion, setControlEndFilterVersion] = useState(0);
    const [tableMountKey, setTableMountKey] = useState(0);
    const dataTableRef = useRef<DataTable | any>(null);
    const prevDataSortRef = useRef<any>(null);
    const hasInitializedPagination = useRef(false);
    const prevHasStartFilterRef = useRef(hasStartFilter);
    const appliedQuestionnaireIdRef = useRef<number | null>(initialQualityControlUrlState.questionnaireId);
    const skipFetchUntilSearchRef = useRef(false);
    const hasUserSearchedRef = useRef(initialQualityControlUrlState.hasAny);
    const skipUrlWriteRef = useRef(true);
    const skipDateResetFromUrlRef = useRef(initialQualityControlUrlState.hasCustomDateRange);
    const pendingUrlFilterSkipsRef = useRef(initialQualityControlUrlState.hasAny ? 1 : 0);
    const skipActiveClickResetRef = useRef(initialQualityControlUrlState.hasAny);
    const useUrlPageOnStartRef = useRef(
        !!(initialQualityControlUrlState.hasAny && initialQualityControlUrlState.page !== 1)
    );
    const hydrateSearchTriggeredRef = useRef(false);

    const statisticSortFields = userQualityControlSortFields;
    const activeColumns = useMemo(() => {
        const fields = [...(statisticSortFields?.fields || [])]
            .filter((item: { isActive: any; name?: string }) => item.isActive && !isRemovedQualityControlStatisticsField(item.name))
            .sort((a: { order: number }, b: { order: number }) => a.order - b.order);

        if (fields.length) {
            return fields;
        }

        return [
            {name: 'qualityControlStatus', order: 1, isActive: true},
            {name: 'qualityControlId', order: 2, isActive: true},
            {name: 'start', order: 3, isActive: true},
        ];
    }, [statisticSortFields]);

    const [sortData, setSortData] = useState<any>({
        pageNumber: initialQualityControlUrlState.page,
        pageSize: initialQualityControlUrlState.size,
        sort: {
            orderBy: orderBy ? orderBy : "Id",
            isDesc: isDescFilter
        },
    });

    function getDefaultEndTime(): Date {
        const d = new Date();
        d.setHours(23, 59, 59, 0);
        return d;
    }

    const formatDate = (dateString: Date) => {
        const dateObj = new Date(dateString);
        const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
        const day = dateObj.getDate().toString().padStart(2, "0");
        const year = dateObj.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const formatDateTime = (dateString: any) => {
        const date = new Date(dateString);
        return `${date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', second: '2-digit'})} ${date.toLocaleDateString('en-GB')}`;
    };

    const getTime = (dateString: Date | any, isEnd?: boolean) => {
        const dateObj = new Date(dateString);
        const hours = dateObj.getHours().toString().padStart(2, "0");
        const minutes = dateObj.getMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}:${isEnd ? `59` : `00`}`;
    };

    const convertSecondsToTime = (totalSeconds: number): string => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const resetTableHorizontalScroll = () => {
        const scrollableBody = document.querySelector('.p-datatable-wrapper');
        if (scrollableBody) {
            scrollableBody.scrollTop = 0;
            scrollableBody.scrollLeft = 0;
        }
    };

    const isMobile = () => windowWidth <= 1248;

    const handleResetPlayer = () => {
        dispatch({type: UPLOAD_AUDIO_LOADING, payload: false});
        dispatch(setAudioUnavailable(false));
        dispatch(getAudioUrlForPlayer(null));
        setKey(key + 1);
    };

    const clearQualityControlResults = () => {
        appliedQuestionnaireIdRef.current = null;
        prevDataSortRef.current = null;
        handleResetPlayer();
        setSelectedRowData(null);
        setVisible(false);
        setFirst(0);
        setTotalItems(0);
        dispatch({type: GET_QUALITY_CONTROL_STATISTICS, payload: []});
        dispatch({
            type: GET_QUALITY_CONTROL_STATISTICS_PAGINATION,
            payload: {
                hasNext: 0,
                pageNumber: 0,
                countPage: 0,
                totalItems: 0,
            },
        });
        dispatch({type: INITIAL_QUALITY_CONTROL_STATISTICS_DATA});
        dispatch(setQualityControlStatisticsCurrentPage(1));
    };

    const handleQuestionnaireChange = (value: number | null) => {
        setSelectedQuestionnaireId(value ?? null);

        if (value != null) {
            return;
        }

        skipFetchUntilSearchRef.current = true;
        hasUserSearchedRef.current = false;
        clearQualityControlResults();
    };

    const handleSearch = (nextValue?: boolean) => {
        hasUserSearchedRef.current = true;
        skipFetchUntilSearchRef.current = false;
        setHasStartFilter((prev) => typeof nextValue === 'boolean' ? nextValue : !prev);
    };

    const handleReset = () => {
        hasUserSearchedRef.current = false;
        skipFetchUntilSearchRef.current = false;
        dataTableRef?.current?.reset();
        customFilter();
        clearQualityControlResults();
        setStartTime(new Date("2024-10-29 00:00:00"));
        setEndTime(() => {
            const d = new Date();
            d.setHours(23, 59, 59, 0);
            return d;
        });
        setFilterDateTime(new Date());
        setFilterEndDateTime(new Date());
        setSortOrder(0);
        setFirst(0);
        dispatch(setQualityControlStatisticsCurrentPage(1));
        dispatch(setQualityControlStatisticsCurrentPageSize(10));
        dispatch(resetQualityControlStatisticFilters());
        setHasStartFilter(!hasStartFilter);
        setAllTimeChecked(false);
        setFilterQualityControlStatus(null);
        setFilterStatus(null);
        setFilterOwnerType(null);
        setFilterForcedClosing(null);
        setFilterBranchName(null);
        setFilterIsManual(null);
        setFilterQualityControlId(null);
        setSelectedQuestionnaireId(null);
        setFilterFullName(null);
        setFilterAni(null);
        setControlEndRange(getDefaultDateRangeFilterValue());
        setHasControlEndFilter(false);
        setSortData({
            pageNumber: 1,
            pageSize: 10,
            sort: {
                orderBy: orderBy ? orderBy : "Id",
                isDesc: isDescFilter
            },
        });
        skipUrlWriteRef.current = true;
        setSearchParams(new URLSearchParams(), {replace: true});
        setTableMountKey((key) => key + 1);
    };

    useEffect(() => {
        if (initialQualityControlUrlState.hasAny) {
            return;
        }
        handleReset();
    }, []);

    useLayoutEffect(() => {
        if (initialQualityControlUrlState.hasAny) {
            if (Object.keys(initialQualityControlUrlState.uiFilters || {}).length > 0) {
                dispatch(setQualityControlStatisticsUiFilters(initialQualityControlUrlState.uiFilters));
            } else {
                dispatch(resetQualityControlStatisticFilters());
            }
            if (initialQualityControlUrlState.page !== 1) {
                dispatch(setQualityControlStatisticsCurrentPage(initialQualityControlUrlState.page));
            }
            if (initialQualityControlUrlState.size !== 10) {
                dispatch(setQualityControlStatisticsCurrentPageSize(initialQualityControlUrlState.size));
            }
            setTableMountKey(1);
            return;
        }

        dispatch(resetQualityControlStatisticFilters());
        setTableMountKey(1);
    }, []);

    useEffect(() => {
        if (!userQualityControlSortFields?.userId && jwtToken?.jwtToken) {
            dispatch(getQualityControlStatisticsUserSortFields(jwtToken.jwtToken));
        }
    }, [userQualityControlSortFields?.userId, jwtToken?.jwtToken]);

    useEffect(() => {
        if (hasUsersAccess) {
            dispatch(getAllSimpleUsers(jwtToken?.jwtToken));
        }
    }, [hasUsersAccess, jwtToken?.jwtToken]);

    useEffect(() => {
        if (hasBranchAccess && jwtToken?.jwtToken) {
            dispatch(getUsersManagerUserBranchesData(jwtToken.jwtToken));
        }
    }, [hasBranchAccess, jwtToken?.jwtToken]);

    useEffect(() => {
        if (!jwtToken?.jwtToken) {
            return;
        }

        fetchSimpleUsersControls(jwtToken.jwtToken)
            .then((response) => {
                setQuestionnaireOptions(normalizeSimpleUsersControls(response.data.data));
            })
            .catch(() => {
                setQuestionnaireOptions([]);
            });
    }, [jwtToken?.jwtToken]);

    useEffect(() => {
        if (pagination.totalItems) {
            setTotalItems(pagination.totalItems);
        } else if (pagination.hasNext && pagination.pageNumber) {
            setTotalItems((pagination.pageNumber * paginationSize) + paginationSize)
        } else if (!pagination.hasNext && pagination.pageNumber) {
            setTotalItems(pagination.pageNumber * paginationSize);
        }
    }, [pagination, paginationSize]);

    useEffect(() => {
        setFirst(0);
    }, [pageChanged]);

    useEffect(() => {
        if (initialQualityControlUrlState.hasAny) {
            return;
        }
        dispatch(setQualityControlStatisticsCurrentPage(1));
        dispatch(setQualityControlStatisticsCurrentPageSize(10));
    }, []);

    useEffect(() => {
        if (!initialQualityControlUrlState.hasAny || hydrateSearchTriggeredRef.current) {
            return;
        }
        if (!jwtToken?.jwtToken) {
            return;
        }
        hydrateSearchTriggeredRef.current = true;
        hasUserSearchedRef.current = true;
        skipFetchUntilSearchRef.current = false;
        appliedQuestionnaireIdRef.current = initialQualityControlUrlState.questionnaireId;
        setHasStartFilter((value) => !value);
    }, [jwtToken?.jwtToken]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        handleResetPlayer();
    }, [selectedRowData]);

    useEffect(() => {
        if (skipDateResetFromUrlRef.current) {
            skipDateResetFromUrlRef.current = false;
            return;
        }
        const minDate = new Date("2023-06-01T00:00:00");

        if (allTimeChecked !== null && allTimeChecked) {
            setStartTime(minDate);
            setEndTime(() => {
                const d = new Date();
                d.setHours(23, 59, 59, 0);
                return d;
            });
            setFilterDateTime(minDate);
            setFilterEndDateTime(getDefaultEndTime());
        } else {
            setStartTime(minDate);
            setEndTime(() => {
                const d = new Date();
                d.setHours(23, 59, 59, 0);
                return d;
            });
            setFilterDateTime(new Date());
            setFilterEndDateTime(getDefaultEndTime());
        }
    }, [allTimeChecked]);

    const onPageChange = (event: { first: React.SetStateAction<number>; page: number; rows: any; }) => {
        setFirst(event.first);
        dispatch(setQualityControlStatisticsCurrentPage(event.page + 1));
        dispatch(setQualityControlStatisticsCurrentPageSize(event.rows));
        setSortData((prev: any) => ({...prev, pageNumber: event.page + 1, pageSize: event.rows}));
    };

    useEffect(() => {
        if (!hasInitializedPagination.current) {
            hasInitializedPagination.current = true;
            return;
        }

        if (!jwtToken?.jwtToken) {
            return;
        }

        const hasStartFilterChanged = prevHasStartFilterRef.current !== hasStartFilter;
        if (skipFetchUntilSearchRef.current) {
            prevHasStartFilterRef.current = hasStartFilter;
            return;
        }

        const filter: { key: string; value: string }[] = [];
        const dataSort: any = {
            pageNumber: paginationPage,
            pageSize: paginationSize,
            sort: {
                orderBy: orderBy ? orderBy : "Id",
                isDesc: isDescFilter
            },
        };

        if (allTimeChecked) {
            setStartTime(new Date("2023-06-01T00:00:00"));
            setEndTime(() => {
                const d = new Date();
                d.setHours(23, 59, 59, 0);
                return d;
            });
            setFilterDateTime(new Date("2023-06-01T00:00:00"));
            setFilterEndDateTime(new Date());
        }

        if (hasUserSearchedRef.current) {
            if (filterQualityControlStatus) {
                filterQualityControlStatus.map((item: any) => filter.push(item));
            }
            if (filterStatus) {
                filterStatus.map((item: any) => filter.push(item));
            }
            if (filterOwnerType) {
                filterOwnerType.map((item: any) => filter.push(item));
            }
            if (filterForcedClosing) {
                filterForcedClosing.map((item: any) => filter.push(item));
            }
            if (filterBranchName) {
                filterBranchName.map((item: any) => filter.push(item));
            }
            if (filterIsManual) {
                filterIsManual.map((item: any) => filter.push(item));
            }

            if (hasStartFilterChanged) {
                appliedQuestionnaireIdRef.current = selectedQuestionnaireId;
            }
            const appliedQuestionnaireId = appliedQuestionnaireIdRef.current;

            if (appliedQuestionnaireId) {
                filter.push({
                    key: 'QualityControlId',
                    value: String(appliedQuestionnaireId),
                });
            }
            if (filterQualityControlId) {
                filterQualityControlId.forEach((item: any) => {
                    if (String(item?.value) !== String(appliedQuestionnaireId)) {
                        filter.push(item);
                    }
                });
            }
            if (filterFullName) {
                filterFullName.map((item: any) => filter.push(item));
            }
            if (filterAni?.value) {
                filter.push(filterAni);
            }
            if (hasControlEndFilter) {
                const fromDate = controlEndRange.fromDate || getDefaultFilterStartDate();
                const toDate = controlEndRange.toDate || getDefaultFilterStartDate();
                const fromTime = controlEndRange.fromTime || getDefaultFilterStartTime();
                const toTime = controlEndRange.toTime || getDefaultFilterEndTime();
                filter.push({
                    key: "ControlEnd",
                    value: `${formatDate(fromDate)} ${getTime(fromTime)},${formatDate(toDate)} ${getTime(toTime, true)}`
                });
            }
        }

        if (filterDateTime && filterEndDateTime && startTime && endTime) {
            filter.push({
                key: "Start",
                value: `${formatDate(filterDateTime)} ${getTime(startTime)},${formatDate(filterEndDateTime)} ${getTime(endTime, true)}`
            });
        } else {
            const currentDateTime = new Date();
            const currentDate = formatDate(currentDateTime);
            filter.push({
                key: "Start",
                value: `${currentDate} 00:00:00,${currentDate} ${getTime(currentDateTime, true)}`
            });
        }

        dataSort.filter = filter;

        const prevDataSort = prevDataSortRef.current;
        const hasChanged = JSON.stringify(prevDataSort) !== JSON.stringify(dataSort);
        prevHasStartFilterRef.current = hasStartFilter;

        if (!hasChanged && !hasStartFilterChanged) return;

        if (hasStartFilterChanged && paginationPage !== 1) {
            if (useUrlPageOnStartRef.current) {
                useUrlPageOnStartRef.current = false;
            } else {
                dataSort.pageNumber = 1;
                setFirst(0);
                dispatch(setQualityControlStatisticsCurrentPage(1));
            }
        }

        prevDataSortRef.current = dataSort;

        const startEntry = dataSort?.filter?.find((item: { key: string; }) => item.key === "Start");
        if (allTimeChecked !== null && startEntry) {
            dispatch(getQualityControlStatisticsWithPaginationFilterAndSorting(dataSort, jwtToken?.jwtToken));
            dispatch(setQualityControlStatisticsFilter(dataSort));
            setSortData(dataSort);
            resetTableHorizontalScroll();
            hasUserSearchedRef.current = true;
        } else {
            dispatch({type: GET_QUALITY_CONTROL_STATISTICS, payload: null})
        }
    }, [paginationPage, paginationSize, orderBy, isDescFilter, sortOrder, filterQualityControlStatus, filterStatus, filterOwnerType, filterForcedClosing, filterBranchName, filterIsManual, filterQualityControlId, filterFullName, filterAni, hasStartFilter, hasControlEndFilter, controlEndFilterVersion]);

    useEffect(() => {
        const fromPath = (activeClick?.from ?? '').replace(/^#/, '');

        if (activeClick?.item && activeClick.item !== '/quality_control') {
            skipFetchUntilSearchRef.current = true;
            clearQualityControlResults();
            return;
        }

        if (activeClick?.item === '/quality_control' && activeClick?.click && fromPath !== '/quality_control' && fromPath !== 'quality_control') {
            if (skipActiveClickResetRef.current) {
                skipActiveClickResetRef.current = false;
                return;
            }
            handleReset();
            return;
        }

        if (activeClick?.item === "/quality_control" && activeClick?.from === '#/quality_control') {
            if (skipFetchUntilSearchRef.current) {
                return;
            }
            dispatch(getQualityControlStatisticsWithPaginationFilterAndSorting(sortData, jwtToken?.jwtToken));
            resetTableHorizontalScroll();
        }
    }, [activeClick]);

    useEffect(() => {
        return () => {
            skipFetchUntilSearchRef.current = true;
            dispatch({type: GET_QUALITY_CONTROL_STATISTICS, payload: []});
            dispatch({
                type: GET_QUALITY_CONTROL_STATISTICS_PAGINATION,
                payload: {
                    hasNext: 0,
                    pageNumber: 0,
                    countPage: 0,
                    totalItems: 0,
                },
            });
            dispatch({type: INITIAL_QUALITY_CONTROL_STATISTICS_DATA});
        };
    }, []);

    const monitoringSort = (event: { sortField: any; }) => {
        const newOrderBy = toQualityControlApiSortField(event.sortField || 'Id');
        if (orderBy === newOrderBy) {
            setIsDescFilter(!isDescFilter);
            setSortOrder(isDescFilter ? 1 : -1);
        } else {
            setOrderBy(newOrderBy);
            setIsDescFilter(true);
            setSortOrder(-1);
        }
    };

    const renderHeader = (field: string, sortField: string) => {
        const sorted = sortField === orderBy;
        const sortIcon = classNames('p-sortable-column-icon', {
            'pi pi-sort-amount-up-alt': sorted && sortOrder === 1,
            'pi pi-fw pi-sort-amount-down': sorted && sortOrder === -1,
            'pi pi-fw pi-sort-alt': sortOrder === 0 || !sorted
        });

        return (
            <div className="flex align-items-center" onClick={() => monitoringSort({'sortField': sortField})}>
                {field} <i className={sortIcon}></i>
            </div>
        );
    };

    const filterClearTemplate = (options: { filterClearCallback: React.MouseEventHandler<HTMLButtonElement> | undefined; field?: string }) => {
        return <Button type="button" className="p-button-secondary" icon="pi pi-times" onClick={(e) => {
            if (options.filterClearCallback) {
                options.filterClearCallback(e);
            }
            const fieldName = (options.field || '').replace(/^item\./, '');
            if (fieldName === 'controlEnd') {
                setControlEndRange(getDefaultDateRangeFilterValue());
                setHasControlEndFilter(false);
                setControlEndFilterVersion((version) => version + 1);
            }
        }}></Button>;
    }

    const filterApplyTemplate = (options: { filterApplyCallback: React.MouseEventHandler<HTMLButtonElement> | undefined; field?: string }) => {
        return <Button type="button" icon="pi pi-check" onClick={(e) => {
            const fieldName = (options.field || '').replace(/^item\./, '');
            if (fieldName === 'controlEnd') {
                hasUserSearchedRef.current = true;
                skipFetchUntilSearchRef.current = false;
                setHasControlEndFilter(true);
                setControlEndFilterVersion((version) => version + 1);
                setFirst(0);
                dispatch(setQualityControlStatisticsCurrentPage(1));
            }
            if (options.filterApplyCallback) {
                options.filterApplyCallback(e as any);
            }
        }}></Button>
    }

    const customFilter = (event?: any) => {
        if (event) {
            hasUserSearchedRef.current = true;
            skipFetchUntilSearchRef.current = false;
            const filters = event.filters;
            setFirst(0);
            dispatch(setQualityControlStatisticsCurrentPage(1));

            const setFilter = (filterName: string, key: string, defaultValue?: any | null) => {
                if (!filters[filterName]) {
                    return defaultValue;
                }

                const value = filters[filterName].value || filters[filterName].constraints?.[0]?.value;
                if (!value) {
                    return defaultValue;
                }

                if (Array?.isArray(value)) {
                    return value.map((item) => ({key, value: String(item)}));
                }

                return {key, value: String(value)};
            };

            setFilterQualityControlStatus((() => {
                const statusFilter = setFilter('qualityControlStatus', 'QualityControlStatus', []);
                if (!Array.isArray(statusFilter) || statusFilter.length === 0) {
                    return statusFilter;
                }

                return expandQualityControlStatusFilterValues(statusFilter.map((item) => item.value))
                    .map((status) => ({key: 'QualityControlStatus', value: String(status)}));
            })());
            setFilterStatus(setFilter('status', 'Status', []));
            setFilterOwnerType(setFilter('ownerType', 'OwnerType', []));
            setFilterForcedClosing(setFilter('forcedClosing', 'ForcedClosing', []));
            setFilterBranchName(setFilter('branchId', 'BranchId', []));
            setFilterIsManual(setFilter('isManual', 'IsManual', []));
            const qualityControlIdFilter = setFilter('qualityControlId', 'QualityControlId', []);
            const qualityControlNameFilter = setFilter('qualityControlName', 'QualityControlId', []);
            setFilterQualityControlId(
                (Array.isArray(qualityControlIdFilter) && qualityControlIdFilter.length)
                    ? qualityControlIdFilter
                    : qualityControlNameFilter
            );
            setFilterFullName(setFilter('fullName', 'UserName', []));
            setFilterAni(() => {
                const ani = filters['ani']?.value || filters['ani']?.constraints?.[0]?.value;
                if (!ani) return null;
                const cleanedValue = String(ani).replace(/[()\-\s\n\t\r+,;!'"?:$%&/]/g, '');
                return {key: 'ANI', value: cleanedValue};
            });
        }
    };

    useEffect(() => {
        if (clearFilters) {
            if (pendingUrlFilterSkipsRef.current > 0) {
                pendingUrlFilterSkipsRef.current -= 1;
                return;
            }
            customFilter({filters: clearFilters});
        }
    }, [clearFilters]);

    useEffect(() => {
        if (skipUrlWriteRef.current) {
            skipUrlWriteRef.current = false;
            return;
        }

        const nextParams = buildQualityControlSearchParams({
            page: paginationPage,
            size: paginationSize,
            orderBy,
            isDesc: isDescFilter,
            sortOrder,
            questionnaireId: selectedQuestionnaireId,
            allTimeChecked: !!allTimeChecked,
            filterQualityControlStatus,
            filterStatus,
            filterOwnerType,
            filterForcedClosing,
            filterBranchName,
            filterIsManual,
            filterQualityControlId,
            filterFullName,
            filterAni,
            filterDateTime,
            filterEndDateTime,
            startTime,
            endTime,
            hasControlEndFilter,
            controlEndFromDate: controlEndRange.fromDate,
            controlEndToDate: controlEndRange.toDate,
            controlEndFromTime: controlEndRange.fromTime,
            controlEndToTime: controlEndRange.toTime,
        });

        if (!areSearchParamsEqual(searchParams, nextParams)) {
            setSearchParams(nextParams, {replace: true});
        }
    }, [
        paginationPage, paginationSize, orderBy, isDescFilter, sortOrder, selectedQuestionnaireId,
        allTimeChecked, filterQualityControlStatus, filterStatus, filterOwnerType, filterForcedClosing,
        filterBranchName, filterIsManual, filterQualityControlId, filterFullName, filterAni,
        filterDateTime, filterEndDateTime, startTime, endTime, hasControlEndFilter, controlEndRange,
        searchParams, setSearchParams
    ]);

    const usersDataWithFullName = usersData?.map((userData: any) => ({
        ...userData,
        fullName: `${userData?.lastName} ${userData?.firstName} ${userData?.middleName ? `${userData.middleName}` : ''}`
    }));

    const qualityControlStatusFilterTemplate = (options: any) => (
        <>
            <div className="mb-3 text-bold">Оберіть статус</div>
            <MultiSelect
                filter
                value={options.value}
                options={QUALITY_CONTROL_STATUS_OPTIONS}
                optionValue="dictionaryId"
                optionLabel="name"
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder="нічого не обрано"
                className="p-column-filter"
            />
        </>
    );

    const statusFilterTemplate = (options: any) => (
        <>
            <div className="mb-3 text-bold">Оберіть статус</div>
            <MultiSelect
                filter
                value={options.value}
                options={statusDictionary || null}
                optionValue="dictionaryId"
                optionLabel="name"
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder="нічого не обрано"
                className="p-column-filter"
            />
        </>
    );

    const ownerTypeFilterTemplate = (options: any) => (
        <>
            <div className="mb-3 text-bold">Оберіть тип</div>
            <MultiSelect
                filter
                value={options.value}
                options={ownerTypeDictionary}
                optionLabel="description"
                optionValue="dictionaryId"
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder="нічого не обрано"
                className="p-column-filter mb-3"
            />
        </>
    );

    const forcedClosingBodyTemplate = (rowData: any) => {
        const foundReason = forcedClosingDictionary?.find((item: any) => item?.dictionaryId === rowData?.item?.forcedClosing);
        return foundReason?.name || '- - -';
    };

    const forcedClosingFilterTemplate = (options: any) => (
        <>
            <div className="mb-3 text-bold">Виберіть:</div>
            <MultiSelect
                filter
                value={options.value}
                options={forcedClosingDictionary}
                optionLabel="name"
                optionValue="dictionaryId"
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder="нічого не обрано"
                className="p-column-filter"
            />
        </>
    );

    const formattedBranches = branches?.map((branch: { id: any; name: any; }) => ({
        ...branch,
        label: `${branch.id} ${branch.name}`,
    }));

    const branchBodyTemplate = (rowData: any) => formatBranchDisplayLabel(rowData?.item, branches) || '- - -';

    const branchFilterTemplate = (options: any) => (
        <>
            <div className="mb-3 text-bold">Оберіть:</div>
            <MultiSelect
                filter
                value={options.value}
                options={formattedBranches}
                optionLabel="label"
                optionValue="id"
                selectedItemsLabel={options?.value?.length ? `Обрано: ${options.value.length}` : "0"}
                maxSelectedLabels={0}
                onChange={(e: any) => options.filterCallback(e.value, options.index)}
                placeholder="нічого не обрано"
                className="p-column-filter"
            />
        </>
    );

    const operatorFilterTemplate = (options: any) => (
        <>
            <div className="mb-3 text-bold">Оберіть оператора</div>
            <MultiSelect
                filter
                value={options.value}
                options={usersDataWithFullName}
                optionValue="userName"
                optionLabel="fullName"
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder="нічого не обрано"
                className="p-column-filter"
            />
        </>
    );

    const aniFilterTemplate = (options: any) => (
        <InputText placeholder="Введіть назву клiента" className="mt-2" value={options.value || ''}
                   onChange={(e: any) => {
                       const cleaned = e.target.value?.replace(/[()\-\s\n\t\r+,;!'"?:$%&/]/g, '');
                       options.filterCallback(cleaned, options.index);
                   }}/>
    );

    const renderStatusColumn = (rowData: any) => {
        const foundStatus = statusDictionary?.filter((status: any) => status?.dictionaryId === rowData?.item?.status);

        const statusMap: {
            [key: string]: string
        } = {
            101: 'p-badge status-active',
            103: 'p-badge status-success',
            104: 'p-badge status-unsuccessful',
            ACTION: 'p-badge status-action',
            102: 'p-badge status-processing'
        };

        const statusClass = statusMap[foundStatus?.[0]?.dictionaryId];

        return (
            <Badge value={foundStatus?.[0]?.name} className={`${statusClass} text-white customer-badge w-6 text-xs p-0`}
                   style={{maxWidth: '95px', minWidth: '95px'}}/>
        );
    };

    const renderQualityControlStatusColumn = (rowData: any) => {
        const status = rowData.item?.qualityControlStatus;
        const statusMeta = getQuestionnaireStatusMeta(status, {isRated: true});

        return (
            <Badge value={statusMeta.label} className={`manual-evaluation-status-badge ${statusMeta.badgeClassName} text-white customer-badge w-6 text-xs p-0`}
                   style={{maxWidth: '110px', minWidth: '110px'}}/>
        );
    };

    const questionnaireBodyTemplate = (rowData: any) => {
        const fullName = rowData.item?.qualityControlFullName;
        if (fullName) {
            return fullName;
        }

        const id = rowData.item?.qualityControlId;
        const name = rowData.item?.qualityControlName;
        if (id && name) {
            return `${id} / ${name}`;
        }
        return name || id || '- - -';
    };

    const getItemValue = (rowData: any, name: string) => {
        const value = rowData?.item?.[name];
        if (value == null || value === '') {
            return '- - -';
        }
        if (typeof value === 'string' && (name.toLowerCase().includes('date') || name === 'start' || name === 'end' || name === 'controlEnd')) {
            const parsed = new Date(value);
            if (!isNaN(parsed.getTime()) && value.includes('-')) {
                return formatDateTime(value);
            }
        }
        if ((name === 'duration' || name === 'fullCallDuration' || name === 'reactionClient' || name === 'reactionOperator') && typeof value === 'number') {
            return convertSecondsToTime(value);
        }
        return value;
    };

    const openRowDetails = (row: any) => {
        const callId = getCallIdFromQualityControlRow(row);
        handleResetPlayer();
        if (callId) {
            dispatch(getQualityControlStatisticToUserDetail(callId, jwtToken?.jwtToken));
        }
        if (!isMobile()) {
            if (props.profileMenuPin === true) {
                setSelectedRowData(row);
                setVisible(true);
            } else {
                setSelectedRowData(row);
            }
        } else {
            setSelectedRowData(row);
            setVisible(true);
        }
    };

    const firstLevelTableCase = (name: string, i: number) => {
        const headerLabel = getQualityControlStatisticsFieldLabel(name);
        const apiSortField = QUALITY_CONTROL_STATISTICS_FILTER_KEYS[name] || toQualityControlApiSortField(name);
        const commonProps = {
            key: i,
            field: `item.${name}`,
            filterField: name,
            showFilterMatchModes: false,
            showFilterMenuOptions: false,
            filterClear: filterClearTemplate,
            filterApply: filterApplyTemplate,
            filterMenuClassName: 'filterStart',
            style: {cursor: 'pointer', width: '100%', minWidth: '165px'} as React.CSSProperties,
        };

        switch (name) {
            case "qualityControlStatus":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, apiSortField)}
                    sortable
                    filter
                    filterElement={qualityControlStatusFilterTemplate}
                    body={renderQualityControlStatusColumn}
                />;
            case "status":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'Status')}
                    sortable
                    filter
                    filterElement={statusFilterTemplate}
                    body={renderStatusColumn}
                    style={{cursor: 'pointer', width: '100%', minWidth: '150px'}}
                />;
            case "callType":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'CallType')}
                    sortable
                    filter={false}
                    filterClear={undefined}
                    filterApply={undefined}
                    body={renderCallTypeActivityIcon}
                    style={{cursor: 'pointer', width: '100%', minWidth: '150px'}}
                />;
            case "ownerType":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'OwnerType')}
                    sortable
                    filter
                    filterElement={ownerTypeFilterTemplate}
                    body={(rowData) => ownerTypeDictionary?.find((item: any) => item?.dictionaryId === rowData?.item?.ownerType)?.description || '- - -'}
                    style={{cursor: 'pointer', width: '100%', minWidth: '170px'}}
                />;
            case "forcedClosing":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'ForcedClosing')}
                    sortable
                    filter
                    filterElement={forcedClosingFilterTemplate}
                    body={forcedClosingBodyTemplate}
                    style={{cursor: 'pointer', width: '100%', minWidth: '180px'}}
                />;
            case "branchId":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'BranchId')}
                    sortable
                    filter={hasBranchAccess}
                    filterElement={hasBranchAccess ? branchFilterTemplate : undefined}
                    filterClear={hasBranchAccess ? filterClearTemplate : undefined}
                    filterApply={hasBranchAccess ? filterApplyTemplate : undefined}
                    body={branchBodyTemplate}
                    style={{cursor: 'pointer', width: '100%', minWidth: '200px'}}
                />;
            case "qualityControlId":
            case "qualityControlName":
            case "qualityControlFullName":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'QualityControlId')}
                    sortable
                    filter={false}
                    filterClear={undefined}
                    filterApply={undefined}
                    body={questionnaireBodyTemplate}
                    style={{cursor: 'pointer', width: '100%', minWidth: '260px'}}
                />;
            case "start":
            case "end":
            case "evaluationDate":
            case "dateTimeEnd":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, apiSortField)}
                    sortable
                    body={(rowData) => rowData?.item?.[name] ? formatDateTime(rowData.item[name]) : '- - -'}
                    style={{cursor: 'pointer', width: '100%', minWidth: '170px'}}
                />;
            case "controlEnd":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'ControlEnd')}
                    sortable
                    filter
                    filterField="controlEnd"
                    filterElement={() => (
                        <DateRangeFilterTemplate
                            value={controlEndRange}
                            onChange={setControlEndRange}
                        />
                    )}
                    body={(rowData) => rowData?.item?.controlEnd ? formatDateTime(rowData.item.controlEnd) : '- - -'}
                    style={{cursor: 'pointer', width: '100%', minWidth: '170px'}}
                />;
            case "fullName":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'UserName')}
                    sortable
                    filter={hasUsersAccess}
                    filterElement={hasUsersAccess ? operatorFilterTemplate : undefined}
                    filterClear={hasUsersAccess ? filterClearTemplate : undefined}
                    filterApply={hasUsersAccess ? filterApplyTemplate : undefined}
                    body={(rowData) => rowData.item?.fullName || '- - -'}
                />;
            case "ani":
                return <Column
                    {...commonProps}
                    header={headerLabel}
                    filter
                    filterElement={aniFilterTemplate}
                    filterPlaceholder="Введіть назву клiента"
                />;
            case "duration":
            case "fullCallDuration":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, apiSortField)}
                    sortable
                    body={(rowData) => rowData?.item?.[name] != null ? convertSecondsToTime(rowData.item[name]) : '- - -'}
                />;
            case "score":
            case "evaluationScore":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, apiSortField)}
                    sortable
                    body={(rowData) => {
                        const score = rowData?.item?.score ?? rowData?.item?.evaluationScore;
                        if (score == null || score === '') {
                            return '- - -';
                        }
                        return `${Number(score)}%`;
                    }}
                    style={{cursor: 'pointer', width: '100%', minWidth: '110px'}}
                />;
            case "evaluationUserName":
            case "createdByUserFullName":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, apiSortField)}
                    sortable
                    body={(rowData) => rowData?.item?.createdByUserFullName || rowData?.item?.evaluationUserName || '- - -'}
                    style={{cursor: 'pointer', width: '100%', minWidth: '170px'}}
                />;
            case "isManual":
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, 'IsManual')}
                    sortable
                    filter
                    filterElement={(options: any) => (
                        <>
                            <div className="mb-3 text-bold">Оберіть тип оцінки</div>
                            <MultiSelect
                                value={options.value}
                                options={QUALITY_CONTROL_EVALUATION_TYPE_OPTIONS}
                                optionValue="value"
                                optionLabel="name"
                                onChange={(e) => options.filterCallback(e.value, options.index)}
                                placeholder="нічого не обрано"
                                className="p-column-filter"
                            />
                        </>
                    )}
                    body={(rowData) => getQualityControlEvaluationTypeLabel(rowData?.item?.isManual)}
                    style={{cursor: 'pointer', width: '100%', minWidth: '150px'}}
                />;
            default:
                return <Column
                    {...commonProps}
                    header={renderHeader(headerLabel, apiSortField)}
                    sortable
                    body={(rowData) => getItemValue(rowData, name)}
                />;
        }
    };

    const renderTableColumns = () => activeColumns
        .map((col: { name: string }, i: number) => firstLevelTableCase(col.name, i))
        .filter(Boolean);

    const dynamicRowClass = (data: { item: any; }) => {
        if (getCallIdFromQualityControlRow(data) === getCallIdFromQualityControlRow(selectedRowData)) {
            return 'mainRowMonitoringTable p-highlight';
        }
        return 'mainRowMonitoringTable';
    };

    return (
        <div className="layout-dashboard">
            <div className="flex flex-wrap col-12 md:col-12">
                <div
                    className={classNames('card widget-table mr-4 col-12 transition-duration-300 monitoring-table align-self-start monitoringLoader', {'md:col-12': !selectedRowData})}>
                    <QualityControlStatisticsListNav
                        profileMenuPin={!!props.profileMenuPin}
                        setAllTimeChecked={setAllTimeChecked}
                        allTimeChecked={allTimeChecked}
                        hasStartFilter={hasStartFilter}
                        setHasStartFilter={handleSearch}
                        setEndTime={setEndTime}
                        endTime={endTime}
                        startTime={startTime}
                        setStartTime={setStartTime}
                        setFilterEndDateTime={setFilterEndDateTime}
                        filterEndDateTime={filterEndDateTime}
                        setFilterDateTime={setFilterDateTime}
                        filterDateTime={filterDateTime}
                        questionnaireOptions={questionnaireOptions}
                        selectedQuestionnaireId={selectedQuestionnaireId}
                        setSelectedQuestionnaireId={handleQuestionnaireChange}
                        userSortFields={userQualityControlSortFields?.fields}
                        sortData={sortData}
                        resetSorting={handleReset}
                    />
                    <DataTable
                        key={tableMountKey}
                        scrollable
                        scrollHeight={selectedRowData && !isMobile() ? '40vh' : '70vh'}
                        ref={dataTableRef}
                        rows={10}
                        onSort={monitoringSort}
                        filters={clearFilters}
                        loading={loader}
                        value={statisticsList}
                        className="p-datatable-customers monitoring-table monitoring-table-rowGroup"
                        rowClassName={dynamicRowClass}
                        selectionMode="single"
                        emptyMessage="Нічого не знайдено"
                        rowHover={false}
                        onFilter={customFilter}
                        lazy
                        onRowClick={(e) => openRowDetails(e.data)}
                        dataKey="item.id"
                    >
                        {renderTableColumns()}
                    </DataTable>
                    <Paginator
                        first={first}
                        rows={paginationSize}
                        totalRecords={totalItems}
                        rowsPerPageOptions={[5, 10, 15, 25, 50]}
                        onPageChange={onPageChange}
                        className="border-top-1 border-bottom-1"
                        template="PrevPageLink PageLinks NextPageLink RowsPerPageDropdown"
                    />
                </div>
                {selectedRowData && statisticData && (
                    <>
                        {windowWidth > 1248 && <div
                            className={classNames('card widget-table col-12 md:col-12 align-self-start sticky top-0')}>
                            <StatisticsListDetails
                                qualityControlMode
                                preferredQuestionnaireId={selectedQuestionnaireId ?? selectedRowData?.item?.qualityControlId ?? null}
                                setSelectedRowData={setSelectedRowData}
                                setSelectedRowExpansion={() => undefined}
                                handleResetPlayer={handleResetPlayer}
                            />
                        </div>}
                        {windowWidth <= 1248 && <div>
                            <Dialog visible={visible} className="monitoring-info" onHide={() => setVisible(false)}>
                                <StatisticsListDetails
                                    qualityControlMode
                                    preferredQuestionnaireId={selectedQuestionnaireId ?? selectedRowData?.item?.qualityControlId ?? null}
                                    setSelectedRowData={setSelectedRowData}
                                    setSelectedRowExpansion={() => undefined}
                                    handleResetPlayer={handleResetPlayer}
                                />
                            </Dialog>
                        </div>}
                    </>
                )}
            </div>
        </div>
    );
};

export default QualityControlStatisticsList;
